import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  NavigateBefore,
  Publish,
  Save,
  Preview,
  CheckCircle,
  Warning,
  Event,
  LocationOn,
  Email,
  Phone,
  Church,
  Person
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FestojoFormData, SECTION_TYPES } from '../../types/festejo';
import { StepComponentProps } from './FestojoFormContainer';
import festojoService from '../../services/festojoService';

interface ReviewFormProps extends StepComponentProps {
  onSaveDraft?: () => void | Promise<void>;
  onPublish?: () => void | Promise<void>;
}

interface ValidationIssue {
  type: 'error' | 'warning';
  message: string;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  formData,
  onPrevious,
  onSaveDraft,
  onPublish,
  isFirstStep,
  isLastStep
}) => {
  const [previewDialogOpen, setPreviewDialogOpen] = useState<boolean>(false);

  // Validar dados completos
  const validateCompleteData = (): ValidationIssue[] => {
    const issues: ValidationIssue[] = [];
    const validation = festojoService.validateFestojoData(formData);

    // Erros críticos
    validation.errors.forEach(error => {
      issues.push({ type: 'error', message: error });
    });

    // Avisos/sugestões
    if (!formData.coverImage) {
      issues.push({ 
        type: 'warning', 
        message: 'Considere adicionar uma imagem de capa para tornar o site mais atrativo' 
      });
    }

    if (formData.sections.length === 0) {
      issues.push({ 
        type: 'warning', 
        message: 'Nenhuma seção foi criada. O site ficará apenas com as informações básicas' 
      });
    }

    if (formData.sections.length < 3) {
      issues.push({ 
        type: 'warning', 
        message: 'Considere adicionar mais seções para enriquecer o conteúdo do site' 
      });
    }

    if (!formData.mapLink) {
      issues.push({ 
        type: 'warning', 
        message: 'Link do mapa não foi adicionado. Isso pode dificultar para os visitantes encontrarem o local' 
      });
    }

    return issues;
  };

  const validationIssues = validateCompleteData();
  const hasErrors = validationIssues.some(issue => issue.type === 'error');
  const hasWarnings = validationIssues.some(issue => issue.type === 'warning');

  // Formatar data para exibição
  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  // Informações básicas do festejo
  const BasicInfoSection: React.FC = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle color="primary" />
        Informações Básicas
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" color="primary" gutterBottom>
            {formData.title}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Em honra a {formData.padroeiro}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <List dense>
            <ListItem>
              <ListItemIcon><Event /></ListItemIcon>
              <ListItemText 
                primary="Período"
                secondary={`${formatDate(formData.startDate)} até ${formatDate(formData.endDate)}`}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Church /></ListItemIcon>
              <ListItemText 
                primary="Paróquia"
                secondary={formData.paroquia}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><LocationOn /></ListItemIcon>
              <ListItemText 
                primary="Endereço"
                secondary={`${formData.endereco}, ${formData.cidade} - ${formData.estado}`}
              />
            </ListItem>
          </List>
        </Grid>

        <Grid item xs={12} md={6}>
          <List dense>
            <ListItem>
              <ListItemIcon><Email /></ListItemIcon>
              <ListItemText 
                primary="Email"
                secondary={formData.contactEmail}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Phone /></ListItemIcon>
              <ListItemText 
                primary="Telefone"
                secondary={formData.contactPhone}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Person /></ListItemIcon>
              <ListItemText 
                primary="URL do Site"
                secondary={`/festejo/${formData.slug}`}
              />
            </ListItem>
          </List>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            {formData.description}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );

  // Seção de aparência
  const AppearanceSection: React.FC = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle color="primary" />
        Aparência
      </Typography>
      
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Box
            sx={{
              width: 40,
              height: 40,
              backgroundColor: formData.theme.primaryColor,
              borderRadius: 1,
              border: '1px solid #ccc'
            }}
          />
        </Grid>
        <Grid item>
          <Typography variant="body2">
            Cor Primária: {formData.theme.primaryColor}
          </Typography>
        </Grid>
        
        <Grid item>
          <Box
            sx={{
              width: 40,
              height: 40,
              backgroundColor: formData.theme.secondaryColor,
              borderRadius: 1,
              border: '1px solid #ccc'
            }}
          />
        </Grid>
        <Grid item>
          <Typography variant="body2">
            Cor Secundária: {formData.theme.secondaryColor}
          </Typography>
        </Grid>

        <Grid item>
          <Typography variant="body2">
            Fonte: {formData.theme.fontFamily}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );

  // Seção de conteúdo
  const ContentSection: React.FC = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle color="primary" />
        Seções do Site ({formData.sections.length})
      </Typography>
      
      {formData.sections.length === 0 ? (
        <Alert severity="info">
          Nenhuma seção foi criada. O site terá apenas as informações básicas.
        </Alert>
      ) : (
        <Box>
          {formData.sections
            .sort((a, b) => a.order - b.order)
            .map((section, index) => {
              const sectionType = SECTION_TYPES[section.type];
              return (
                <Box key={section.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 20 }}>
                    {index + 1}.
                  </Typography>
                  <Chip
                    icon={<span>{sectionType.icon}</span>}
                    label={sectionType.label}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="body2">
                    {section.title}
                  </Typography>
                </Box>
              );
            })}
        </Box>
      )}
    </Paper>
  );

  // Seção de validação
  const ValidationSection: React.FC = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {hasErrors ? <Warning color="error" /> : <CheckCircle color="success" />}
        Status de Validação
      </Typography>
      
      {validationIssues.length === 0 ? (
        <Alert severity="success">
          ✅ Todos os dados estão corretos! O festejo está pronto para ser publicado.
        </Alert>
      ) : (
        <Box>
          {validationIssues.map((issue, index) => (
            <Alert 
              key={index} 
              severity={issue.type} 
              sx={{ mb: 1 }}
            >
              {issue.message}
            </Alert>
          ))}
        </Box>
      )}
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Revisão e Publicação
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Revise todas as informações antes de publicar o site do festejo
      </Typography>

      {/* Seções de revisão */}
      <BasicInfoSection />
      <AppearanceSection />
      <ContentSection />
      <ValidationSection />

      {/* Ações */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Próximos Passos
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Preview />}
              onClick={() => setPreviewDialogOpen(true)}
              size="large"
            >
              Visualizar Site
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
              Veja como o site ficará
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Save />}
              onClick={onSaveDraft}
              size="large"
            >
              Salvar Rascunho
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
              Salvar sem publicar
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Publish />}
              onClick={onPublish}
              disabled={hasErrors}
              size="large"
              color="primary"
            >
              Publicar Festejo
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
              Tornar público na internet
            </Typography>
          </Grid>
        </Grid>

        {hasErrors && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Corrija os erros acima antes de publicar o festejo.
          </Alert>
        )}
      </Paper>

      {/* Botões de navegação */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onPrevious}
          startIcon={<NavigateBefore />}
          size="large"
        >
          Anterior
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onSaveDraft}
            startIcon={<Save />}
            size="large"
          >
            Salvar Rascunho
          </Button>
          
          <Button
            variant="contained"
            onClick={onPublish}
            disabled={hasErrors}
            startIcon={<Publish />}
            size="large"
          >
            Publicar
          </Button>
        </Box>
      </Box>

      {/* Dialog de preview */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Preview do Site
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta é uma prévia de como o site ficará. Para ver o resultado final completo, 
            publique o festejo e acesse o link gerado.
          </Alert>
          
          {/* Aqui você poderia incluir um componente de preview real */}
          <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              {formData.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Preview completo disponível após publicação
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewForm;

